import {
  Button,
  Checkbox,
  Col,
  Form,
  FormInstance,
  Input,
  Radio,
  Row,
  Select,
  Space,
  message,
} from "antd";
import React, { useEffect, useState } from "react";
import { editClientAddress, resolveClientAlternative } from "../../../apis/client.api";
import { getAddressFromPincode } from "../../../apis/misc.api";
import { MapAddressExport, ProcurementMapPicker } from "../../misc/map-picker";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import { INDIAN_STATES, canonicalState } from "../../../constants/indian_states.constants";
interface EditClientAddressFormProps {
  address: any;
  fetchAddresses: Function;
  onClientEditAddressClose: Function;
}
const { TextArea } = Input;
const { Option } = Select;

const EditClientAddressForm = ({
  address,
  fetchAddresses,
  onClientEditAddressClose,
}: EditClientAddressFormProps) => {
  const [form] = Form.useForm();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission("client.update");

  const [, setBillingAddressTrue] = useState(false);
  const [validGoogleMapLink, setValidGoogleMapLink] = useState(false);
  const [latLongLoading] = useState(false);
  const [selectedMapAddress, setSelectedMapAddress] =
    useState<MapAddressExport>();

  // A billing address is a bill-to record, never a delivery destination — the
  // backend splits the two on exactly this flag (is_billing=False *is* the
  // shipping set). The fields that only exist to price and plan a delivery
  // (building type, floor, lift, the map pin) are asked for on shipping
  // addresses only; a GST-registered address knows none of them.
  const isBilling = !!Form.useWatch("is_billing", form);

  const onFinish = async (updatedValues: any) => {
    if (!canEdit) {
      message.error("You don't have permission to edit this address");
      return;
    }

    const gstNumber = updatedValues?.gst_no;
    if (gstNumber) {
      if (gstNumber === "29AARCM7928H1ZO") {
        message.error({
          content: "Cannot use this GST number as it's MD's GST number",
          key: "save",
        });
        return;
      }
    }

    const client_alternative = isBilling
      ? null
      : await resolveClientAlternative(
          address?.user,
          updatedValues?.contact_person_name,
          updatedValues?.contact_person_contact
        );

    editClientAddress(
      { ...updatedValues, ...(client_alternative ? { client_alternative } : {}) },
      address?.id
    ).then((res) => {
      fetchAddresses();
      onClientEditAddressClose();
    });
  };

  const SubmitButton = ({ form }: { form: FormInstance }) => {
    const [submittable, setSubmittable] = React.useState(false);

    // Watch all values
    const values = Form.useWatch([], form);

    React.useEffect(() => {
      form.validateFields({ validateOnly: true }).then(
        () => {
          setSubmittable(true);
        },
        () => {
          setSubmittable(false);
        }
      );
    }, [values]);

    // A billing address created from a verified GST has no coordinates — the
    // backend never geocodes one — so requiring a pin here left those rows
    // permanently uneditable.
    return (
      <Button
        type="primary"
        htmlType="submit"
        disabled={!submittable || (!isBilling && !validGoogleMapLink) || !canEdit}
        loading={latLongLoading}
      >
        Submit
      </Button>
    );
  };

  useEffect(() => {
    Object.keys(address).map((key: any) => {
      form.setFieldValue(
        key,
        key === "state"
          ? canonicalState(address[key]) || address[key]
          : address[key]
      );
    });

    // Country is not stored consistently on older rows; the storefront pins it
    // to India and shows it read-only, so do the same here.
    form.setFieldValue("country", "India");
    if (address?.lift_available != null) {
      form.setFieldValue("lift_available", Boolean(address.lift_available));
    }

    setSelectedMapAddress({
      lat: address?.lat,
      long: address?.long,
      address: address?.address,
      city: address?.city,
      id: address?.id,
      name: address?.name,
      pincode: address?.pincode,
      country: address?.country,
      state: address?.state,
      google_map_address: `https://www.google.com/maps/place/${address?.lat},${address?.long}`,
    });

    if (
      address?.lat &&
      address?.long &&
      String(address?.lat)?.length !== 0 &&
      String(address?.long)?.length !== 0
    ) {
      setValidGoogleMapLink(true);
    }
  }, [address]);

  // Mirrors the storefront modal: a pincode only ever fills city/state, and the
  // country stays pinned to India. It must not touch the address label — that
  // used to be overwritten with whatever the pincode lookup was named.
  const setAddressFromPincode = (pincode: any) => {
    if (pincode?.length == 6) {
      getAddressFromPincode(pincode).then((res) => {
        if (res) {
          form.setFieldsValue({
            state: canonicalState(res?.state),
            city: res?.city,
            country: "India",
          });
        } else {
          message.error("Pincode doesn't exist");
        }
      });
    }
  };
  const gstChange = (e: any) => {
    let gst = e?.target?.value;
    if (gst?.length == 15) {
      form.setFieldValue("pan_no", gst?.slice(2, 12));
    }
  };

  return (
    <div>
      <Form
        form={form}
        name="validateOnly"
        layout="vertical"
        autoComplete="off"
        onFinish={onFinish}
      >
        <Form.Item label="" name="is_billing" valuePropName="checked">
          <Checkbox
            onChange={() => {
              setBillingAddressTrue(true);
            }}
          >
            Billing address
          </Checkbox>
        </Form.Item>
        <Form.Item
          name="name"
          rules={[
            {
              required: true,
            },
          ]}
          label="Address Label"
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="contact_person_name"
          rules={[
            {
              required: true,
            },
          ]}
          label="Contact person name"
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="contact_person_contact"
          rules={[
            {
              required: true,
              pattern: /^[6-9]\d{9}$/gi,
              message: "Enter a valid Phone",
            },
          ]}
          label="Contact person phone"
        >
          <Input />
        </Form.Item>

        {address?.gst_no && (
          <Form.Item
            name="gst_no"
            rules={[
              {
                pattern:
                  /\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/g,
                message: "Enter a valid GST Number",
              },
            ]}
            label="GST"
          >
            <Input onChange={gstChange} />
          </Form.Item>
        )}

        <ProcurementMapPicker
          onPick={(selectedMapAddress) => {
            setSelectedMapAddress(selectedMapAddress);
            Object.entries(selectedMapAddress).forEach(([key, value]) => {
              form.setFieldValue(key, value);
            });
            form.setFieldValue("country", "India");
            // Google hands back its own spelling ("Karnataka", "TS", "Orissa") —
            // canonicalise it or the state Select has nothing to match on.
            form.setFieldValue(
              "state",
              canonicalState(selectedMapAddress?.state) ||
                selectedMapAddress?.state
            );
            setValidGoogleMapLink(true);
          }}
          openInModal={window.innerWidth > 768}
          pickedAddress={selectedMapAddress}
          defaultMapOpen={false}
        />

        <Space>
          <Form.Item
            rules={[
              {
                required: !isBilling,
              },
            ]}
            label="Building Type"
            name={"address_type"}
            hidden={isBilling}
            style={{ minWidth: 140, flexShrink: 0 }}
          >
            <Select placeholder="Select building type">
              <Option value="villa">Villa</Option>
              <Option value="apartment">Apartment</Option>
              <Option value="office">Office</Option>
              <Option value="restaurant">Restaurant</Option>
              <Option value="factory">Factory</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>
          {/* On a billing address these two are whatever the GST portal returned
              (plot_no / floor_no), and the portal often returns neither — so they
              are shown but never gate the save. */}
          <Form.Item
            name="flat_no"
            rules={[
              {
                required: !isBilling,
              },
            ]}
            label="Tower / Flat No. / Plot No."
          >
            <Input />
          </Form.Item>
          {/* On a shipping address this is always asked for, whatever the
              building type — the floor drives the unloading charge, so a villa
              still has to say "0". */}
          <Form.Item
            name="floor_no"
            rules={[
              {
                required: !isBilling,
              },
              {
                pattern: /^\d+$/,
                message: "Enter a valid floor number",
              },
            ]}
            label="Floor No."
          >
            <Input
              inputMode="numeric"
              onChange={(e) =>
                form.setFieldValue(
                  "floor_no",
                  e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "")
                )
              }
            />
          </Form.Item>
        </Space>
        <Form.Item
          name="lift_available"
          label="Lift Available"
          hidden={isBilling}
          rules={[
            { required: !isBilling, message: "Select if a lift is available" },
          ]}
        >
          <Radio.Group optionType="button">
            <Radio.Button value={true}>Yes</Radio.Button>
            <Radio.Button value={false}>No</Radio.Button>
          </Radio.Group>
        </Form.Item>
        {/* Read-only on purpose, exactly as on the storefront: the address text
            and the map pin are only ever allowed to travel together. Change it
            by picking a new location above. */}
        <Form.Item
          name="address"
          rules={[
            {
              required: true,
              message: "Pick a location on the map to fill the address",
            },
          ]}
          label="Full Address"
          extra="Filled from the map pin — pick a location above to change it"
        >
          <TextArea rows={4} readOnly />
        </Form.Item>
        <Space>
          <Form.Item
            name="pincode"
            rules={[
              {
                required: true,
                pattern: /^[1-9]\d{5}$/gi,
                message: "Enter a valid pincode",
              },
            ]}
            label="Pincode"
          >
            <Input
              onChange={(e) => {
                setAddressFromPincode(e.target.value);
              }}
            />
          </Form.Item>
          <Form.Item
            name="city"
            rules={[
              {
                required: true,
              },
            ]}
            label="City"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="state"
            rules={[
              {
                validator: (_, value) =>
                  canonicalState(value)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Select a valid state from the list")
                      ),
              },
            ]}
            label="State"
          >
            <Select
              showSearch
              placeholder="Select state"
              optionFilterProp="children"
              style={{ minWidth: 180 }}
            >
              {INDIAN_STATES.map((name) => (
                <Option key={name} value={name}>
                  {name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="country" rules={[{ required: true }]} label="Country">
            <Input readOnly />
          </Form.Item>
        </Space>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="lat"
              rules={[
                {
                  required: !isBilling,
                },
              ]}
              label="Latitude"
            >
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="long"
              rules={[
                {
                  required: !isBilling,
                },
              ]}
              label="Longitude"
            >
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <Space>
            <SubmitButton form={form} />
            {/* <Button htmlType="reset">Reset</Button> */}
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default EditClientAddressForm;
